import { arrayOf, func, string, node, instanceOf } from "prop-types";
import React from "react";
import { Button, Header, Icon } from "semantic-ui-react";
import ProductOptions from "shared/components/roomBook/ProductOptions";
import { navigationButton } from "shared/helpers/navigationButton";
import CardGrid from "shared/components/cardGrid/CardGrid";
import LineItemPicker from "shared/components/roomBook/pickers/LineItemPicker";
import { FormattedMessage } from "react-intl";
import { ConfiguratorSubLineItemShape } from "shared/shapes/configuratorSubLineItem.shape";
import { RoomBookController } from "shared/actions/roomBookController";
import { ConfiguratorLineItemShape } from "shared/shapes/configuratorLineItem.shape";

class OptionsMenu extends React.PureComponent {
  getSelectLineItem = lineItem => () => {
    const { onSelectLineItemId } = this.props;
    onSelectLineItemId(lineItem.id);
  };

  getActivatedLineItem = lineItem => {
    return {
      key: lineItem.id,
      title: lineItem.title,
      imageUrl: lineItem.thumbUrl,
      imageClass: lineItem.imageClass,
      total: lineItem.total,
      isBasePrice: !lineItem.isActivated,
      isPriceOnRequest: lineItem.isPriceOnRequest,
      button: navigationButton(lineItem),
      modelType: "line_item",
      onClick: this.getSelectLineItem(lineItem)
    };
  };

  getReplacementLineItem = (lineItem, picker) => {
    return {
      key: lineItem.id,
      title: lineItem.title,
      imageUrl: lineItem.thumbUrl,
      imageClass: lineItem.imageClass,
      total: lineItem.totalMin,
      isPriceOnRequest: lineItem.isPriceOnRequest,
      isBasePrice: !lineItem.isActivated,
      button: navigationButton(lineItem),
      onClick: () => {
        picker
          .select(lineItem)
          .then(this.getSelectLineItem(lineItem), () => {});
      }
    };
  };

  getOptionalLineItem = (lineItem, picker) => {
    return {
      key: lineItem.id,
      title: lineItem.title,
      imageUrl: lineItem.thumbUrl,
      imageClass: lineItem.imageClass,
      total: lineItem.totalMin,
      isPriceOnRequest: lineItem.isPriceOnRequest,
      isBasePrice: !lineItem.isActivated,
      button: navigationButton(lineItem),
      onClick: () => {
        picker
          .select(lineItem)
          .then(this.getSelectLineItem(lineItem), () => {});
      }
    };
  };

  getLineItems(picker) {
    const { lineItems } = this.props;
    return lineItems.map(lineItem => {
      if (lineItem.isActivated) {
        return this.getActivatedLineItem(lineItem);
      }
      if (lineItem.isReplacement) {
        return this.getReplacementLineItem(lineItem, picker);
      }
      return this.getOptionalLineItem(lineItem, picker);
    });
  }

  getSubLineItems() {
    const { subLineItems, selectedLineItem } = this.props;

    return subLineItems
      .filter(subLineItem => subLineItem.lineItemId === selectedLineItem.id)
      .map(subLineItem => ({
        key: subLineItem.id,
        title: subLineItem.title,
        imageUrl: subLineItem.thumbUrl,
        imageClass: "productImage",
        isPriceOnRequest: subLineItem.isPriceOnRequest,
        isBasePrice: subLineItem.isOptional && !subLineItem.isActivated,
        total: subLineItem.total || subLineItem.totalMin,
        button: navigationButton({ ...subLineItem }),
        onClick: () => {
          this.handleSelectSubLineItem({ subLineItemId: subLineItem.id });
        }
      }));
  }

  handleSelectSubLineItem = ({ subLineItemId }) => {
    const { onSelectSubLineItemId } = this.props;
    onSelectSubLineItemId(subLineItemId);
  };

  handleBackButton = () => {
    const {
      onSelectSubLineItemId,
      onSelectLineItemId,
      selectedSubLineItem,
      selectedLineItem
    } = this.props;
    if (
      selectedLineItem?.isOptional &&
      !selectedLineItem.isActivated &&
      (!selectedSubLineItem || selectedLineItem.subLineItemIds.length === 1)
    ) {
      this.deactivateLineItemDialog(true);
    } else if (selectedSubLineItem) {
      onSelectSubLineItemId(null);
    } else {
      onSelectLineItemId(null);
    }
  };

  deactivateLineItemId(lineItemId) {
    const { subLineItems, ctrl } = this.props;
    const subLineItemIds = subLineItems
      .filter(sli => sli.lineItemId === lineItemId)
      .map(sli => sli.id);
    return ctrl
      .deselectProducts(subLineItemIds)
      .then(() => ctrl.deactivateLineItem(lineItemId));
  }

  deactivateLineItemDialog(noSelectionWarning = false) {
    const {
      confirmationDialog,
      selectedLineItem,
      onSelectLineItemId
    } = this.props;
    const titleId = noSelectionWarning
      ? "buyerPortal.roomBook.optionalLineItem.actions.noSelectionWarning.title"
      : "buyerPortal.roomBook.optionalLineItem.actions.remove.title";
    const messageId = noSelectionWarning
      ? "buyerPortal.roomBook.optionalLineItem.actions.noSelectionWarning.message"
      : "buyerPortal.roomBook.optionalLineItem.actions.remove.message";
    confirmationDialog({
      title: <FormattedMessage id={titleId} values={{ ...selectedLineItem }} />,
      content: (
        <FormattedMessage id={messageId} values={{ ...selectedLineItem }} />
      ),
      buttons: [
        {
          id: "remove",
          label: "meta.actions.unlink",
          onClick: cb =>
            this.deactivateLineItemId(selectedLineItem.id).then(() => {
              cb();
              onSelectLineItemId(null);
            })
        },
        {
          id: "cancel",
          label: "meta.actions.cancel",
          basic: true
        }
      ]
    });
  }

  renderHeader() {
    const { selectedSubLineItem, selectedLineItem, roomName } = this.props;
    let title;
    if (selectedSubLineItem) {
      title = selectedSubLineItem.title;
    } else if (selectedLineItem) {
      title = selectedLineItem.title;
    } else {
      title = roomName;
    }

    return <Header inverted content={title} />;
  }

  renderActions() {
    const {
      selectedSubLineItem,
      subLineItems,
      selectedLineItem,
      lineItems
    } = this.props;
    const showBackButton =
      (selectedSubLineItem && subLineItems.length > 1) ||
      (selectedLineItem && lineItems.length > 1);
    const showRemoveButton =
      selectedLineItem &&
      selectedLineItem.isOptional &&
      !selectedLineItem.isProgressed &&
      (!selectedSubLineItem || selectedLineItem.subLineItemIds.length === 1);

    return (
      <Button.Group widths={2}>
        {showBackButton && (
          <Button onClick={this.handleBackButton}>
            <Icon name="arrow left" />
            <FormattedMessage id="meta.actions.back" defaultMessage="Zurück" />
          </Button>
        )}
        {showRemoveButton && (
          <Button onClick={() => this.deactivateLineItemDialog()}>
            <Icon name="remove" />
            <FormattedMessage
              id="buyerPortal.roomBook.optionalLineItem.actions.remove.title"
              defaultMessage="Sonderwunschpaket {title} entfernen"
              values={{ title: "" }}
            />
          </Button>
        )}
      </Button.Group>
    );
  }

  renderBody() {
    const { selectedSubLineItem, selectedLineItem } = this.props;

    if (selectedSubLineItem) {
      return this.renderProductOptions();
    }
    if (selectedLineItem) {
      return this.renderSubLineItemMenu();
    }
    return this.renderLineItemMenu();
  }

  renderLineItemMenu() {
    return (
      <LineItemPicker>
        {picker => <CardGrid items={this.getLineItems(picker)} />}
      </LineItemPicker>
    );
  }

  renderSubLineItemMenu() {
    return <CardGrid items={this.getSubLineItems()} />;
  }

  renderProductOptions() {
    const { selectedSubLineItem, subLineItems, onSelectOption } = this.props;

    return (
      <ProductOptions
        onSelectOption={onSelectOption}
        renderMissingMappingIcon
        renderMetaThumbs={false}
        renderQuantityLabel={false}
        subLineItem={subLineItems.find(
          sli => sli.id === selectedSubLineItem.id
        )}
      />
    );
  }

  render() {
    const { children } = this.props;

    return (
      <div data-component="optionsMenu">
        {this.renderHeader()}
        {children}
        {this.renderBody()}
        {this.renderActions()}
      </div>
    );
  }
}

OptionsMenu.propTypes = {
  subLineItems: arrayOf(ConfiguratorSubLineItemShape),
  lineItems: arrayOf(ConfiguratorLineItemShape),
  onSelectLineItemId: func.isRequired,
  onSelectSubLineItemId: func.isRequired,
  onSelectOption: func.isRequired,
  roomName: string.isRequired,
  children: node,
  selectedSubLineItem: ConfiguratorSubLineItemShape,
  selectedLineItem: ConfiguratorLineItemShape,
  ctrl: instanceOf(RoomBookController).isRequired
};

OptionsMenu.defaultProps = {
  selectedSubLineItem: null,
  selectedLineItem: null,
  children: null,
  subLineItems: [],
  lineItems: []
};

export default OptionsMenu;
